/* eslint-disable max-lines */
import { Button, Card, Dialog, FormStatus, H2, H3, H4, InfoCard, P, Section, Space, Span } from '@dnb/eufemia';
import {
  clock_medium as ClockIcon,
  cloud_transfer_medium as CloudTransferIcon,
  file as FileIcon,
  lightbulb_medium as LightbulbIcon,
  trash as TrashIcon,
} from '@dnb/eufemia/icons';
import { ApiType } from '@portals/shared/portal/ApiDto';
import { Decision } from '@portals/shared/portal/AppApiEntitlementDto';
import { type AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import type { TeamDetailsDto } from '@portals/shared/portal/TeamDto';
import { TrackRoute } from '@portals/shared-frontend/components';
import Markdown from '@portals/shared-frontend/components/Markdown';
import { useAsync } from '@portals/shared-frontend/hooks';
import { hasOwnProperty } from '@portals/shared-frontend/utils';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { useMemo, useState } from 'react';
import { Link, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { deleteApp, detachApi } from '@/api/app';
import AuthTemplate from '@/components/AuthTemplate';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import Page from '@/components/Page';
import RouterBreadcrumb from '@/components/RouterBreadcrumb';
import Toast from '@/components/Toast';
import { useUser } from '@/hooks/useUser';
import ProviderApiCard from '@/pages/profile/apps/application/components/ProviderApiCard';

import ApplicationDetails from './components/ApplicationDetails';
import Credential from './components/Credential';
import Entitlement from './components/Entitlement';
import EntitlementRequest from './components/EntitlementRequest';

import style from './Application.module.css';

export enum TabKey {
  CREDENTIALS = 'credentials',
  COLLABORATORS = 'collaborators',
}

const CURL_CREATE_API_WITH_API_KEYS = `
\`\`\`bash
curl --location --request POST 'https://api.prod.devportal.tech-03.net/v1/apis' \n \
--header 'X-DNBAPI-Trace-Id: {{trace-id}}' \n \
--header 'Content-Type: application/json' \n \
--header 'x-app-id: {{app-id}}' \n \
--header 'x-api-key: {{api-key}}' \n \
--data '{
    "slug": "<string>",
    "name": "<string>",
    "classification": "<string>",
    "description": "<string>",
    "enableStatus": <bool>
}'
\`\`\`
`;

const CURL_CREATE_API_WITH_OAUTH2 = `
\`\`\`bash
curl --location --request POST 'https://api.prod.devportal.tech-03.net/v1/apis' \n \
--header 'X-DNBAPI-Trace-Id: {{trace-id}}' \n \
--header 'Content-Type: application/json' \n \
--header 'Authorization: Bearer {{accessToken}}' \n \
--header 'x-app-id: {{app-id}}' \n \
--data '{
    "slug": "<string>",
    "name": "<string>",
    "classification": "<string>",
    "description": "<string>",
    "enableStatus": <bool>
}'
\`\`\`
`;

export default function Application() {
  const navigate = useNavigate();
  const { user, reload: reloadUser } = useUser();
  const { id, teamId } = useParams();
  const [deleteAppError, setDeleteAppError] = useState('');
  const [apiKeyError, setApiKeyError] = useState('');
  const [detachApiError, setDetachApiError] = useState<string | null>(null);

  const { data: app, isValidating: appLoading, mutate: mutateApp } = useSWR<AppWithEntitlementsDto>(`/apps/${id}`);

  const { data: team, isValidating: teamLoading } = useSWR<TeamDetailsDto>(teamId ? `/team/${teamId}` : null);

  const apisForApp = useMemo(
    () =>
      app?.providerApis
        ? app.providerApis.filter(Boolean).sort((api1, api2) => api1.name.localeCompare(api2.name))
        : [],
    [app?.providerApis],
  );

  const entitlementRequests = app ? app.notCompletedEntitlementRequests : [];

  const pendingEntitlementRequestApiIds = new Set(
    entitlementRequests
      .filter((req) => req.decision === Decision.PENDING || req.decision === Decision.LEVEL1_APPROVED)
      .map(({ apiId }) => apiId),
  );
  const deniedEntitlementRequestApiIds = new Set(
    entitlementRequests.filter((req) => req.decision === Decision.DENIED).map(({ apiId }) => apiId),
  );

  const onDetachApi = useAsync(
    async (appId: string, apiId: string) => {
      try {
        await detachApi(appId, apiId);
        mutateApp();
      } catch (error) {
        if (ApiError.isApiError(error)) {
          setDetachApiError(error.message);
          setTimeout(() => {
            setDetachApiError(null);
          }, 5000);
        } else {
          throw error;
        }
      }
    },
    [mutateApp],
  );

  const onDelete = useAsync(
    async (appId: string) => {
      try {
        await deleteApp(appId);
        if (teamId) {
          navigate(`/team/${teamId}`);
        } else {
          reloadUser();
          navigate('/profile/apps');
        }
      } catch (error) {
        if (hasOwnProperty(error, 'message')) {
          setDeleteAppError(
            typeof error.message === 'string' ? error.message : 'Something went wrong, please try again later',
          );
        } else {
          setDeleteAppError('Something went wrong, please try again later');
        }
      }
    },
    [navigate, teamId],
  );

  const isAdmin = useMemo(() => {
    if (app && app.owner.type !== 'team') {
      return true;
    }
    return Boolean(team && team.members.some((member) => member.email === user?.email && member.isAdmin));
  }, [team, app, user]);

  return (
    <AuthTemplate>
      <Page skeleton={appLoading || teamLoading} spacing={false} styleType="white" title={app?.name}>
        {app && teamId && team?.name && (
          <TrackRoute
            name={app.name}
            parents={[{ path: `/team/${teamId}`, name: team.name, type: 'team' }]}
            path={`/team/${teamId}/application/${id}`}
            type="app"
          />
        )}
        {(onDelete.waiting || onDetachApi.waiting) && <LoadingModal />}

        <Container>
          <Section backgroundColor="var(--color-black-3)" spacing="large">
            <RouterBreadcrumb
              data={[
                {
                  icon: 'chevron_left',
                  text: teamId ? `Team page` : `Profile page`,
                  href: teamId ? `/team/${teamId}` : `/profile/apps`,
                },
              ]}
              goBackText={teamId ? 'Team page' : 'Profile page'}
            />
            {app && (
              <>
                {entitlementRequests.length > 0 && (
                  <Section backgroundColor="var(--color-black-3)" spacing="large">
                    <H3 bottom="small">
                      <ClockIcon /> Active requests
                    </H3>
                    <EntitlementRequest entitlementRequests={entitlementRequests} isAdmin={isAdmin} />
                  </Section>
                )}
                <Section top="large">
                  <ApplicationDetails
                    app={app}
                    isAdmin={isAdmin}
                    owner={{
                      ...app.owner,
                      name: team ? team?.name : `${user?.firstName} ${user?.lastName}`,
                      type: team ? 'team' : 'personal',
                    }}
                  />
                </Section>
                {app.isProviderApp && (
                  <Section top="x-large">
                    <H3 bottom="small">
                      <CloudTransferIcon /> Integration services this app provides
                    </H3>
                    {apisForApp.length === 0 ? (
                      <Card direction="vertical" outset={false} stretch>
                        <H4>
                          <Span right="small">
                            <LightbulbIcon />
                          </Span>
                          Get started with your first API
                        </H4>
                        <P top="small">
                          You have access to the Developer Portal API. How about getting started with publishing your
                          first API?
                        </P>

                        <Button
                          element={RouterLink}
                          target="_blank"
                          to="/documentation/developer-portal-api/@default/@latest/guide"
                          top="small"
                          variant="secondary"
                        >
                          Go to guide
                        </Button>
                        <P top="small">Example</P>
                        <Markdown>
                          {app.accessType === 'API_KEYS' ? CURL_CREATE_API_WITH_API_KEYS : CURL_CREATE_API_WITH_OAUTH2}
                        </Markdown>
                      </Card>
                    ) : (
                      <div className={style['card-list']}>
                        {apisForApp.map((api) => (
                          <ProviderApiCard api={api} key={api.id} />
                        ))}
                      </div>
                    )}
                  </Section>
                )}
                <Space top="x-large">
                  <Credential
                    app={app}
                    error={apiKeyError}
                    isAdmin={isAdmin}
                    onApiKeyCreated={async (apiKey) => {
                      await mutateApp((appData) => {
                        if (appData) {
                          return {
                            ...appData,
                            apiKeys: [...appData.apiKeys, apiKey],
                          };
                        }
                        return;
                      }, false);
                    }}
                    onApiKeyDeleted={async (apiKeyId) => {
                      await mutateApp((appData) => {
                        if (appData) {
                          return {
                            ...appData,
                            apiKeys: appData.apiKeys.filter((apiKey) => apiKey.id !== apiKeyId),
                          };
                        }
                        return;
                      }, false);
                    }}
                    onClientDeleted={async (clientId) => {
                      await mutateApp((appData) => {
                        if (appData) {
                          return {
                            ...appData,
                            clients: appData.clients.filter((client) => client.id !== clientId),
                          };
                        }
                        return;
                      }, false);
                    }}
                    onSetError={(error) => setApiKeyError(error)}
                  />
                </Space>

                <Section top="x-large">
                  <H3 bottom="small">
                    <CloudTransferIcon /> Integration service access granted to this app
                  </H3>
                  {!app.isProviderApp && (
                    <div className={style['integration-service-button-container']}>
                      {isAdmin && (
                        <Button
                          icon="add"
                          icon_position="left"
                          on_click={() =>
                            navigate(
                              app.owner.type === 'team'
                                ? `/team/${app.owner.id}/application/${app.id}/attach-api`
                                : `/application/${app.id}/attach-api`,
                            )
                          }
                        >
                          Request new access
                        </Button>
                      )}
                      {app.currentApiAndScopeEntitlements.some(
                        (entitlement) => entitlement.api.type === ApiType.EVENT,
                      ) && (
                        <Button
                          element={Link}
                          icon={<FileIcon />}
                          icon_position="left"
                          to="asyncapi"
                          variant="secondary"
                        >
                          View AsyncAPI
                        </Button>
                      )}
                    </div>
                  )}

                  {app.currentApiAndScopeEntitlements.length === 0 ? (
                    <InfoCard
                      centered
                      stretch
                      text="It seems like you have not attached any integration services to your application. Click the button above to get started."
                      top="small"
                    />
                  ) : (
                    app.currentApiAndScopeEntitlements
                      .sort((a, b) => (a.api.name.toUpperCase() < b.api.name.toUpperCase() ? -1 : 1))
                      .map((entitlement) => (
                        <Space key={entitlement.api.id} top="small">
                          <Entitlement
                            app={app}
                            entitlement={entitlement}
                            hasDeniedRequest={deniedEntitlementRequestApiIds.has(entitlement.api.id)}
                            hasPendingRequest={pendingEntitlementRequestApiIds.has(entitlement.api.id)}
                            isAdmin={isAdmin}
                            onDetachApi={(appId, apiId) => {
                              if (!app.isProviderApp) {
                                onDetachApi.execute(appId, apiId);
                              }
                            }}
                          />
                        </Space>
                      ))
                  )}
                </Section>
              </>
            )}
          </Section>
          {isAdmin && (
            <Section backgroundColor="var(--color-lavender)" spacing="x-large">
              <H2 top="medium">Delete App</H2>
              <P bottom="medium" top="small">
                This will permanently delete your app and its accesses.
              </P>
              <Dialog
                confirmText="Delete app"
                confirmType="warning"
                declineText="Cancel"
                description="This will permanently delete your app and accesses. If you need the accesses again, then you will have to re-apply for it."
                icon={TrashIcon}
                key="delete-app"
                onConfirm={async ({ close }) => {
                  close();
                  app && (await onDelete.execute(app.id));
                }}
                title="Are you sure you want to delete the app?"
                triggerAttributes={{
                  text: 'Delete app',
                  icon: TrashIcon,
                  size: 'large',
                  bottom: 'medium',
                }}
                variant="confirmation"
              />
              <FormStatus text={deleteAppError} />
            </Section>
          )}
          {detachApiError && <Toast message={detachApiError} position="bottom" variant="error" />}
        </Container>
      </Page>
    </AuthTemplate>
  );
}
